import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DEFAULT_LOCALE, FALLBACK_OF, type Locale, asLocale } from './locales';
import { type PartialDictionary, type TranslationKey, enGB } from './dictionaries/en-GB';
import { en } from './dictionaries/en';

/**
 * The application's translation layer.
 *
 * ## Why a provider rather than string replacement
 *
 * The tempting shortcut is to walk the DOM and swap text on language change.
 * It breaks the moment anything renders after the swap — a modal, a lazily
 * mounted section, a route — because those nodes never existed when the walk
 * ran, and it corrupts any text that happens to match. Rendering *from* the
 * locale means every subtree, present or future, is correct by construction:
 * React re-renders, and there is one source of truth.
 *
 * ## Resolution order
 *
 * `locale -> FALLBACK_OF[locale] -> ... -> en-GB`. A locale supplies only the
 * strings that differ; anything missing resolves up the chain, and `en-GB` is
 * typed complete so the chain always terminates in a real string. A key that
 * does not exist is a compile error, not a runtime blank.
 *
 * ## Lazy dictionaries
 *
 * `DICTIONARIES` is a static map today because two English dictionaries are
 * smaller than the code to fetch them. The loader is deliberately shaped so a
 * dictionary can become `() => import('./dictionaries/fr-FR')` without any
 * component changing: `setLocale` is already async-tolerant, and the provider
 * already renders through a resolved-dictionary state rather than reading the
 * module directly.
 */

const DICTIONARIES: Readonly<Record<Locale, PartialDictionary>> = {
  'en-GB': enGB,
  en,
};

/** Where the choice is remembered between visits. */
export const LOCALE_STORAGE_KEY = 'jt.locale';

export interface I18nValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  /**
   * Looks a key up in the active locale, falling back down the chain.
   *
   * `vars` interpolates `{name}` placeholders. Deliberately not a template
   * engine: a translator should never be able to execute anything, and a
   * placeholder with no matching variable is left visible rather than blanked,
   * so the gap shows up in review instead of shipping as a hole in a sentence.
   */
  readonly t: (key: TranslationKey, vars?: Readonly<Record<string, string | number>>) => string;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

/**
 * Reads the persisted choice.
 *
 * Wrapped because storage access *throws* rather than returning null when a
 * browser blocks site data, and an exception here would take the whole
 * application down before it painted.
 */
function readStoredLocale(): Locale | undefined {
  try {
    return asLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function resolve(locale: Locale, key: TranslationKey): string {
  let current: Locale | undefined = locale;

  while (current !== undefined) {
    const value = DICTIONARIES[current][key];
    if (value !== undefined) return value;
    current = FALLBACK_OF[current];
  }

  // Unreachable while `en-GB` is typed complete, and typed as such. Returning
  // the key rather than throwing keeps a future incomplete fallback from
  // blanking a page.
  return key;
}

function interpolate(template: string, vars?: Readonly<Record<string, string | number>>): string {
  if (vars === undefined) return template;

  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(vars, name) ? String(vars[name]) : whole,
  );
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  readonly children: ReactNode;
  /** Lets tests and future server rendering supply the locale directly. */
  readonly initialLocale?: Locale;
}): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? readStoredLocale() ?? DEFAULT_LOCALE,
  );

  // `lang` drives hyphenation, quote marks, spell-check and how a screen
  // reader pronounces the page. Setting it is not cosmetic.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // Private browsing, or site data blocked. The choice still applies for
      // this visit; only its persistence is lost, which is not worth an error.
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => interpolate(resolve(locale, key), vars),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === undefined) {
    // Louder than a silent English fallback: a component rendered outside the
    // provider would look correct in English and fail only once translated.
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return value;
}

/** The common case: just the lookup function. */
export function useT(): I18nValue['t'] {
  return useI18n().t;
}
